import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';
import GiveawayCard from '../components/GiveawayCard';
import ShowcaseComments from '../components/ShowcaseComments';
import VouchModal from '../components/VouchModal';
import UserListModal from '../components/UserListModal';
import Badge from '../components/Badge';
import { initStarsParallax, destroyStarsParallax } from '../utils/starsParallax';
import '../index.css';

const Profile = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useContext(AuthContext);
  const [profile, setProfile] = useState(null);
  const [giveaways, setGiveaways] = useState([]);
  const [showcase, setShowcase] = useState([]);
  const [posts, setPosts] = useState([]);
  const [postImageIndices, setPostImageIndices] = useState({});
  const [showcaseImageIndices, setShowcaseImageIndices] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('posts');
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [vouches, setVouches] = useState([]);
  const [vouchStats, setVouchStats] = useState(null);
  const [winners, setWinners] = useState([]);
  const [showVouchModal, setShowVouchModal] = useState(false);
  const [winnersStats, setWinnersStats] = useState(null);
  const [featuredAchievements, setFeaturedAchievements] = useState([]);
  const [hallOfFameStatus, setHallOfFameStatus] = useState({ categories: [], badges: [] });
  const [lists, setLists] = useState([]);
  const [imageError, setImageError] = useState(false);
  const [vouchImageErrors, setVouchImageErrors] = useState({});
  
  // Modal states
  const [showFollowersModal, setShowFollowersModal] = useState(false);
  const [showFollowingModal, setShowFollowingModal] = useState(false);
  const [modalUsers, setModalUsers] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  
  // Ref for profile card to attach parallax animation
  const profileCardRef = useRef(null);
  const parallaxInstanceRef = useRef(null);
  
  const isOwnProfile = isAuthenticated && user && user.username === username;
  const isAdmin = isAuthenticated && user && user.accountType === 'admin';

  // Helper functions for cosmetics
  const getAvatarFrameClass = () => {
    if (!profile?.equippedCosmetics?.avatarFrame) return '';
    const frameId = profile.equippedCosmetics.avatarFrame;
    const frameMap = {
      'frame-neon-purple': 'avatar-frame-neon-purple',
      'frame-gold-elite': 'avatar-frame-gold-elite',
      'frame-rgb-animated': 'avatar-frame-rgb-animated',
      'frame-fire': 'avatar-frame-fire',
      'frame-ice': 'avatar-frame-ice',
      'frame-electric-storm': 'avatar-frame-electric-storm',
      'frame-dragon-scale': 'avatar-frame-dragon-scale',
      'frame-cosmic-orbit': 'avatar-frame-cosmic-orbit',
      'frame-shadow-realm': 'avatar-frame-shadow-realm',
      'frame-cherry-blossom': 'avatar-frame-cherry-blossom',
      'frame-toxic-glow': 'avatar-frame-toxic-glow',
      'frame-holographic': 'avatar-frame-holographic',
      'frame-lava-flow': 'avatar-frame-lava-flow',
      'frame-digital-glitch': 'avatar-frame-digital-glitch',
      'frame-celestial': 'avatar-frame-celestial'
    };
    return frameMap[frameId] || '';
  };
  
  const getUsernameEffectClass = () => {
    if (!profile?.equippedCosmetics?.usernameEffect) return '';
    const effectId = profile.equippedCosmetics.usernameEffect;
    const effectMap = {
      'username-rainbow': 'username-rainbow',
      'username-glow-gold': 'username-glow-gold',
      'username-shadow-deep': 'username-shadow-deep',
      'username-neon-cyan': 'username-neon-cyan',
      'username-metallic': 'username-metallic',
      'username-fire-blaze': 'username-fire-blaze',
      'username-ice-frost': 'username-ice-frost',
      'username-electric-spark': 'username-electric-spark',
      'username-toxic-drip': 'username-toxic-drip',
      'username-hologram': 'username-hologram',
      'username-glitch': 'username-glitch',
      'username-crystalline': 'username-crystalline',
      'username-plasma': 'username-plasma',
      'username-vintage': 'username-vintage',
      'username-neon-pink': 'username-neon-pink'
    };
    return effectMap[effectId] || '';
  };
  
  const getBadgeIcons = () => {
    const badgeMap = {
      // Shop cosmetic badges
      'badge-crown': '👑',
      'badge-fire': '🔥',
      'badge-star': '⭐',
      'badge-gem': '💎',
      'badge-skull': '💀',
      'badge-rocket': '🚀',
      // Achievement badges
      'badge-og-member': '🏆',
      'badge-giveaway-legend': '🎁',
      'badge-social-star': '⭐',
      'badge-collector': '💎',
      'badge-game-master': '🎮',
      'badge-speedrunner': '🏃',
      'badge-night-owl': '🦉',
      'badge-achievement-hunter': '🎯',
      'badge-community-hero': '🦸',
      'badge-code-breaker': '🔐',
      'badge-strategist': '🧠',
      'badge-completionist': '📋',
      'badge-mvp': '👑',
      'badge-digital-artist': '🎨',
      'badge-beta-tester': '🔬'
    };
    
    if (!profile?.equippedCosmetics?.badges || profile.equippedCosmetics.badges.length === 0) return [];
    
    return profile.equippedCosmetics.badges.map(badgeId => badgeMap[badgeId] || '').filter(Boolean);
  };

  const getCardBackgroundClass = () => {
    if (!profile?.equippedCosmetics?.cardBackground) return '';
    // The ID from the database already matches the CSS class name (e.g., 'card-bg-gradient-sunset')
    // Cosmetic CSS classes use !important to override theme defaults
    return profile.equippedCosmetics.cardBackground;
  };

  const getCardBorderClass = () => {
    if (!profile?.equippedCosmetics?.cardBorder) return '';
    // The ID from the database already matches the CSS class name (e.g., 'card-border-elegant-gold')
    // Cosmetic CSS classes use !important to override theme defaults
    return profile.equippedCosmetics.cardBorder;
  };

  const fetchProfile = useCallback(async () => {
    try {
      const response = await axios.get(`/api/profile/${username}`);
      setProfile(response.data);
      
      // Check follow status if authenticated and not own profile
      if (isAuthenticated && !isOwnProfile) {
        const token = localStorage.getItem('token');
        const followStatus = await axios.get(`/api/follow/${response.data._id}/follow-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsFollowing(followStatus.data.data.isFollowing);
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError(err.response?.data?.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [username, isAuthenticated, isOwnProfile]);

  const fetchGiveaways = useCallback(async () => {
    try {
      const response = await axios.get(`/api/profile/${username}/giveaways`);
      setGiveaways(response.data);
    } catch (err) {
      console.error('Error fetching giveaways:', err);
    }
  }, [username]);

  const fetchShowcase = useCallback(async () => {
    try {
      const response = await axios.get(`/api/showcase/${username}`);
      setShowcase(response.data);
      // Initialize image indices for all showcase items
      const initialIndices = {};
      response.data.forEach(item => {
        initialIndices[item._id] = 0;
      });
      setShowcaseImageIndices(initialIndices);
    } catch (err) {
      console.error('Error fetching showcase:', err);
    }
  }, [username]);

  const fetchPosts = useCallback(async () => {
    try {
      const response = await axios.get(`/api/posts/user/${username}`);
      setPosts(response.data);
      // Initialize image indices for all posts
      const initialIndices = {};
      response.data.forEach(item => {
        initialIndices[item._id] = 0;
      });
      setPostImageIndices(initialIndices);
    } catch (err) {
      console.error('Error fetching posts:', err);
    }
  }, [username]);

  const fetchVouches = useCallback(async () => {
    try {
      if (!profile) return;
      const response = await axios.get(`/api/vouches/user/${profile._id}`);
      setVouches(response.data.data);
      setVouchStats(response.data.stats);
    } catch (err) {
      console.error('Error fetching vouches:', err);
    }
  }, [profile]);

  const fetchWinners = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/giveaways/user/my-winners', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWinners(response.data.data);
      setWinnersStats(response.data.stats);
    } catch (err) {
      console.error('Error fetching winners:', err);
    }
  }, []);

  const fetchFeaturedAchievements = useCallback(async () => {
    try {
      const response = await axios.get(`/api/achievements/featured/${username}`);
      setFeaturedAchievements(response.data.data);
    } catch (err) {
      console.error('Error fetching featured achievements:', err);
    }
  }, [username]);

  const fetchHallOfFameStatus = useCallback(async () => {
    try {
      if (!profile) return;
      const response = await axios.get(`/api/hall-of-fame/user/${profile._id}`);
      setHallOfFameStatus(response.data.data);
    } catch (err) {
      console.error('Error fetching hall of fame status:', err);
    }
  }, [profile]);

  const fetchLists = useCallback(async () => {
    try {
      if (!profile) return;
      const token = localStorage.getItem('token');
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const response = await axios.get(`/api/game-lists/user/${profile._id}`, config);
      setLists(response.data.data);
    } catch (err) {
      console.error('Error fetching lists:', err);
    }
  }, [profile]);

  useEffect(() => {
    fetchProfile();
    fetchFeaturedAchievements();
  }, [fetchProfile, fetchFeaturedAchievements]);

  useEffect(() => {
    if (profile) {
      fetchHallOfFameStatus();
    }
  }, [profile, fetchHallOfFameStatus]);

  useEffect(() => {
    if (activeTab === 'posts') {
      fetchPosts();
    } else if (activeTab === 'giveaways') {
      fetchGiveaways();
    } else if (activeTab === 'showcase') {
      fetchShowcase();
    } else if (activeTab === 'vouches' && profile) {
      fetchVouches();
    } else if (activeTab === 'winners' && isOwnProfile) {
      fetchWinners();
    } else if (activeTab === 'lists' && profile) {
      fetchLists();
    }
  }, [activeTab, username, profile, fetchPosts, fetchGiveaways, fetchShowcase, fetchVouches, fetchWinners, fetchLists, isOwnProfile]);

  // Initialize Stars Parallax background when card-bg-stars-parallax is equipped
  useEffect(() => {
    if (profileCardRef.current && profile?.equippedCosmetics?.cardBackground === 'card-bg-stars-parallax') {
      // Small delay to ensure the card is fully rendered
      const timer = setTimeout(() => {
        if (profileCardRef.current && !parallaxInstanceRef.current) {
          parallaxInstanceRef.current = initStarsParallax(profileCardRef.current);
        }
      }, 100);
      
      return () => {
        clearTimeout(timer);
        if (parallaxInstanceRef.current) {
          destroyStarsParallax(parallaxInstanceRef.current);
          parallaxInstanceRef.current = null;
        }
      };
    } else {
      // Clean up if background changes
      if (parallaxInstanceRef.current) {
        destroyStarsParallax(parallaxInstanceRef.current);
        parallaxInstanceRef.current = null;
      }
    }
  }, [profile?.equippedCosmetics?.cardBackground]);

  const changeShowcaseImage = (showcaseId, direction) => {
    const item = showcase.find(s => s._id === showcaseId);
    if (!item || !item.images || item.images.length <= 1) return;

    setShowcaseImageIndices(prev => {
      const currentIndex = prev[showcaseId] || 0;
      const maxIndex = item.images.length - 1;
      let newIndex;

      if (direction === 'next') {
        newIndex = currentIndex >= maxIndex ? 0 : currentIndex + 1;
      } else {
        newIndex = currentIndex <= 0 ? maxIndex : currentIndex - 1;
      }

      return { ...prev, [showcaseId]: newIndex };
    });
  };

  const changePostImage = (postId, direction) => {
    setPostImageIndices(prev => {
      const currentIndex = prev[postId] || 0;
      const item = posts.find(p => p._id === postId);
      if (!item || !item.images || item.images.length <= 1) return prev;
      
      const maxIndex = item.images.length - 1;
      let newIndex;

      if (direction === 'next') {
        newIndex = currentIndex >= maxIndex ? 0 : currentIndex + 1;
      } else {
        newIndex = currentIndex <= 0 ? maxIndex : currentIndex - 1;
      }

      return { ...prev, [postId]: newIndex };
    });
  };

  const handleUpvote = async (showcaseId) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/showcase/${showcaseId}/upvote`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Refresh showcase to get updated upvote count
      fetchShowcase();
    } catch (err) {
      console.error('Error toggling upvote:', err);
    }
  };

  const handleDeleteShowcase = async (showcaseId) => {
    if (!window.confirm('Are you sure you want to delete this showcase item?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/showcase/${showcaseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchShowcase();
    } catch (err) {
      console.error('Error deleting showcase:', err);
      alert('Failed to delete showcase item');
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchPosts();
    } catch (err) {
      console.error('Error deleting post:', err);
      alert('Failed to delete post');
    }
  };

  const handleFollowToggle = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    setFollowLoading(true);
    try {
      const token = localStorage.getItem('token');
      const endpoint = isFollowing 
        ? `/api/follow/${profile._id}/unfollow`
        : `/api/follow/${profile._id}/follow`;
      
      if (isFollowing) {
        await axios.delete(endpoint, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(endpoint, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      setIsFollowing(!isFollowing);
      // Refresh profile to update follower count
      fetchProfile();
    } catch (err) {
      console.error('Error toggling follow:', err);
      alert('Failed to update follow status');
    } finally {
      setFollowLoading(false);
    }
  };

  const handleMessageUser = () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    navigate(`/messages?userId=${profile._id}`);
  };

  const handleShowFollowers = async () => {
    setShowFollowersModal(true);
    setModalLoading(true);
    setModalUsers([]);
    try {
      const response = await axios.get(`/api/follow/${profile._id}/followers`);
      setModalUsers(response.data.data.followers || []);
    } catch (err) {
      console.error('Error fetching followers:', err);
    } finally {
      setModalLoading(false);
    }
  };

  const handleShowFollowing = async () => {
    setShowFollowingModal(true);
    setModalLoading(true);
    setModalUsers([]);
    try {
      const response = await axios.get(`/api/follow/${profile._id}/following`);
      setModalUsers(response.data.data.following || []);
    } catch (err) {
      console.error('Error fetching following:', err);
    } finally {
      setModalLoading(false);
    }
  };

  const getPlatformIcon = (platform) => {
    const icons = {
      twitter: '🐦',
      bluesky: '🦋',
      twitch: '🎮',
      youtube: '▶️',
      instagram: '📸',
      tiktok: '🎵',
      facebook: '👍',
      discord: '💬',
      reddit: '🔴',
      steam: '🎮',
      kick: '🦵',
      website: '🌐',
      other: '🔗'
    };
    return icons[platform] || '🔗';
  };

  const getPlatformColor = (platform) => {
    const colors = {
      twitter: '#1DA1F2',
      bluesky: '#1185fe',
      twitch: '#9146FF',
      youtube: '#FF0000',
      instagram: '#E4405F',
      tiktok: '#000000',
      facebook: '#1877F2',
      discord: '#5865F2',
      reddit: '#FF4500',
      steam: '#171A21',
      kick: '#53FC18',
      website: '#4CAF50',
      other: '#666666'
    };
    return colors[platform] || '#666666';
  };

  const getAccountTypeEmoji = (accountType) => {
    const emojiMap = {
      'regular': '🎮',
      'indie_dev': '👨‍💻',
      'content_creator': '🎬',
      'giveaway_host': '🎁',
      'steam_curator': '🎯',
      'admin': '👑'
    };
    return emojiMap[accountType] || '🎮';
  };

  const getAccountTypeBadge = (accountType) => {
    const badges = {
      'regular': { label: 'Gamer', gradient: 'linear-gradient(135deg, #4CAF50, #45a049)', emoji: '🎮' },
      'indie_dev': { label: 'Indie Dev', gradient: 'linear-gradient(135deg, #9C27B0, #7B1FA2)', emoji: '👨‍💻' },
      'content_creator': { label: 'Content Creator', gradient: 'linear-gradient(135deg, #FF6B6B, #EE5A6F)', emoji: '🎬' },
      'giveaway_host': { label: 'Giveaway Host', gradient: 'linear-gradient(135deg, #3B82F6, #2563EB)', emoji: '🎁' },
      'steam_curator': { label: 'Steam Curator', gradient: 'linear-gradient(135deg, #1B2838, #2A475E)', emoji: '🎯' },
      'admin': { label: 'Admin', gradient: 'linear-gradient(135deg, #FF5722, #E64A19)', emoji: '👑' }
    };
    return badges[accountType] || badges['regular'];
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '1.2rem', color: 'var(--text-dark)' }}>Loading profile...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '1.2rem', color: 'var(--accent-red)', marginBottom: '20px' }}>{error}</div>
        <button
          onClick={() => navigate('/')}
          style={{
            padding: '12px 24px',
            backgroundColor: 'var(--accent-green)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: 'bold'
          }}
        >
          Go Home
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Profile Header */}
      {/* Cosmetic classes override theme styles via !important. 
          Theme-based inline styles only apply when NO cosmetics are equipped. */}
      <div ref={profileCardRef} className={`${getCardBackgroundClass()} ${getCardBorderClass()}`} style={{
        ...((!profile?.equippedCosmetics?.cardBackground) && {
          background: `linear-gradient(135deg, var(--profile-gradient-start) 0%, var(--profile-gradient-end) 100%)`
        }),
        borderRadius: '20px',
        padding: '40px',
        marginBottom: '30px',
        ...(!profile?.equippedCosmetics?.cardBorder && {
          border: `1px solid var(--modal-border)`
        }),
        ...(!profile?.equippedCosmetics?.cardBorder && {
          boxShadow: 'var(--shadow-hover)'
        }),
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{ display: 'flex', alignItems: 'start', gap: '30px', flexWrap: 'wrap' }}>
          {/* Avatar */}
          <div style={{
            width: '140px',
            flexShrink: 0,
            textAlign: 'center'
          }}>
            <div style={{
              width: '140px',
              height: '140px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #4CAF50, #45a049)',
              padding: '4px',
              display: 'inline-block'
            }}>
              <div className={getAvatarFrameClass()} style={{
                width: '132px',
                height: '132px',
                borderRadius: '50%',
                backgroundColor: 'var(--card-bg)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '3.5rem',
                overflow: 'hidden',
                border: '3px solid var(--border-color)'
              }}>
                {profile.profile?.avatar?.url && !imageError ? (
                  <img
                    src={profile.profile.avatar.url}
                    alt={username}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <span style={{ color: 'var(--accent-green)' }}>{username.charAt(0).toUpperCase()}</span>
                )}
              </div>
            </div>
            
            {/* Display Equipped Badges */}
            {getBadgeIcons().length > 0 && (
              <div className="cosmetic-badges-container" style={{ marginTop: '10px' }}>
                {getBadgeIcons().map((icon, index) => (
                  <span key={index} className="cosmetic-badge" title="Cosmetic Badge">
                    {icon}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* User Info */}
          <div style={{ flex: 1, minWidth: '300px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '15px', flexWrap: 'wrap' }}>
              <h1 className={getUsernameEffectClass()} style={{ 
                margin: 0, 
                fontSize: '2.5rem', 
                color: 'var(--text-primary)',
                fontWeight: '700',
                letterSpacing: '-0.5px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <span>{getAccountTypeEmoji(profile.accountType)}</span>
                {username}
                {profile.isBot && (
                  <span style={{
                    padding: '4px 12px',
                    background: 'linear-gradient(135deg, #6c757d 0%, #495057 100%)',
                    color: 'white',
                    borderRadius: '15px',
                    fontSize: '0.75rem',
                    fontWeight: '700',
                    letterSpacing: '0.5px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    marginLeft: '5px'
                  }}>
                    🤖 BOT
                  </span>
                )}
              </h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                {profile.accountType && (
                  <span style={{
                    padding: '8px 16px',
                    background: getAccountTypeBadge(profile.accountType).gradient,
                    color: 'white',
                    borderRadius: '25px',
                    fontSize: '0.9rem',
                    fontWeight: 'bold',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                  }}>
                    {getAccountTypeBadge(profile.accountType).emoji} {getAccountTypeBadge(profile.accountType).label}
                  </span>
                )}
                
                {/* Verification Badges */}
                {profile.emailVerified && (
                  <span style={{
                    padding: '6px 12px',
                    background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                    color: 'white',
                    borderRadius: '20px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    boxShadow: '0 2px 8px rgba(40, 167, 69, 0.3)'
                  }}>
                    ✓ Email
                  </span>
                )}
                {profile.socialAccounts?.steam?.verified && (
                  <span style={{
                    padding: '6px 12px',
                    background: 'linear-gradient(135deg, #171a21 0%, #1b2838 100%)',
                    color: 'white',
                    borderRadius: '20px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.4)'
                  }}>
                    🎮 Steam
                  </span>
                )}
                {profile.socialAccounts?.discord?.verified && (
                  <span style={{
                    padding: '6px 12px',
                    background: 'linear-gradient(135deg, #7289da 0%, #5865f2 100%)',
                    color: 'white',
                    borderRadius: '20px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    boxShadow: '0 2px 8px rgba(114, 137, 218, 0.3)'
                  }}>
                    💬 Discord
                  </span>
                )}
                {profile.socialAccounts?.twitter?.verified && (
                  <span style={{
                    padding: '6px 12px',
                    background: 'linear-gradient(135deg, #1da1f2 0%, #0d95e8 100%)',
                    color: 'white',
                    borderRadius: '20px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    boxShadow: '0 2px 8px rgba(29, 161, 242, 0.3)'
                  }}>
                    🐦 Twitter
                  </span>
                )}
                {profile.socialAccounts?.bluesky?.verified && (
                  <span style={{
                    padding: '6px 12px',
                    background: 'linear-gradient(135deg, #1185fe 0%, #0a7adb 100%)',
                    color: 'white',
                    borderRadius: '20px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    boxShadow: '0 2px 8px rgba(17, 133, 254, 0.3)'
                  }}>
                    🦋 Bluesky
                  </span>
                )}
                
                {/* Hall of Fame Badges */}
                {hallOfFameStatus.categories && hallOfFameStatus.categories.length > 0 && (
                  <>
                    {hallOfFameStatus.categories.includes('union_amplifiers') && (
                      <span style={{
                        padding: '6px 12px',
                        background: 'linear-gradient(135deg, #FF6B6B 0%, #FF5252 100%)',
                        color: 'white',
                        borderRadius: '20px',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        boxShadow: '0 2px 8px rgba(255, 107, 107, 0.4)',
                        border: '2px solid rgba(255, 255, 255, 0.3)'
                      }}>
                        📢 Union Amplifier
                      </span>
                    )}
                    {hallOfFameStatus.categories.includes('giveaway_legends') && (
                      <span style={{
                        padding: '6px 12px',
                        background: 'linear-gradient(135deg, #4ECDC4 0%, #44B4B0 100%)',
                        color: 'white',
                        borderRadius: '20px',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        boxShadow: '0 2px 8px rgba(78, 205, 196, 0.4)',
                        border: '2px solid rgba(255, 255, 255, 0.3)'
                      }}>
                        🎁 Giveaway Legend
                      </span>
                    )}
                    {hallOfFameStatus.categories.includes('vision_shapers') && (
                      <span style={{
                        padding: '6px 12px',
                        background: 'linear-gradient(135deg, #FFD93D 0%, #FFC107 100%)',
                        color: '#000',
                        borderRadius: '20px',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        boxShadow: '0 2px 8px rgba(255, 217, 61, 0.4)',
                        border: '2px solid rgba(255, 255, 255, 0.3)'
                      }}>
                        💡 Vision Shaper
                      </span>
                    )}
                    {hallOfFameStatus.categories.includes('growth_architects') && (
                      <span style={{
                        padding: '6px 12px',
                        background: 'linear-gradient(135deg, #95E1D3 0%, #7FD4C4 100%)',
                        color: '#000',
                        borderRadius: '20px',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        boxShadow: '0 2px 8px rgba(149, 225, 211, 0.4)',
                        border: '2px solid rgba(255, 255, 255, 0.3)'
                      }}>
                        🚀 Growth Architect
                      </span>
                    )}
                    {hallOfFameStatus.categories.includes('union_backers_bronze') && (
                      <span style={{
                        padding: '6px 12px',
                        background: 'linear-gradient(135deg, #CD7F32 0%, #A86728 100%)',
                        color: 'white',
                        borderRadius: '20px',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        boxShadow: '0 2px 8px rgba(205, 127, 50, 0.4)',
                        border: '2px solid rgba(255, 255, 255, 0.3)'
                      }}>
                        🥉 Union Backer
                      </span>
                    )}
                    {hallOfFameStatus.categories.includes('union_backers_silver') && (
                      <span style={{
                        padding: '6px 12px',
                        background: 'linear-gradient(135deg, #C0C0C0 0%, #A8A8A8 100%)',
                        color: '#000',
                        borderRadius: '20px',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        boxShadow: '0 2px 8px rgba(192, 192, 192, 0.4)',
                        border: '2px solid rgba(255, 255, 255, 0.3)'
                      }}>
                        🥈 Union Backer
                      </span>
                    )}
                    {hallOfFameStatus.categories.includes('union_backers_gold') && (
                      <span style={{
                        padding: '6px 12px',
                        background: 'linear-gradient(135deg, #FFD700 0%, #FFC700 100%)',
                        color: '#000',
                        borderRadius: '20px',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        boxShadow: '0 2px 8px rgba(255, 215, 0, 0.4)',
                        border: '2px solid rgba(255, 255, 255, 0.3)'
                      }}>
                        🥇 Union Backer
                      </span>
                    )}
                    {hallOfFameStatus.categories.includes('union_backers_platinum') && (
                      <span style={{
                        padding: '6px 12px',
                        background: 'linear-gradient(135deg, #E5E4E2 0%, #D0CFCE 100%)',
                        color: '#000',
                        borderRadius: '20px',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        boxShadow: '0 2px 8px rgba(229, 228, 226, 0.4)',
                        border: '2px solid rgba(255, 255, 255, 0.3)'
                      }}>
                        💎 Union Backer
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>

            {profile.profile?.bio && (
              <p style={{ 
                color: 'var(--text-secondary)', 
                lineHeight: '1.7', 
                marginBottom: '20px',
                fontSize: '1.05rem'
              }}>
                {profile.profile.bio}
              </p>
            )}

            {/* Stats */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
              gap: '20px', 
              marginTop: '20px',
              padding: '20px',
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.05)'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ 
                  fontSize: '2rem', 
                  fontWeight: 'bold', 
                  background: 'linear-gradient(135deg, #4CAF50, #45a049)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  {profile.giveawaysHosted || 0}
                </div>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-gray)', marginTop: '4px', fontWeight: '500' }}>Giveaways Hosted</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ 
                  fontSize: '2rem', 
                  fontWeight: 'bold', 
                  background: 'linear-gradient(135deg, #2196F3, #1976D2)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  {profile.profile?.giveawaysWon || 0}
                </div>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-gray)', marginTop: '4px', fontWeight: '500' }}>Giveaways Won</div>
              </div>
              <div 
                style={{ textAlign: 'center', cursor: 'pointer' }}
                onClick={handleShowFollowers}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
              >
                <div style={{ 
                  fontSize: '2rem', 
                  fontWeight: 'bold', 
                  background: 'linear-gradient(135deg, #9C27B0, #7B1FA2)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  {profile.followerCount || 0}
                </div>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-gray)', marginTop: '4px', fontWeight: '500' }}>Followers</div>
              </div>
              <div 
                style={{ textAlign: 'center', cursor: 'pointer' }}
                onClick={handleShowFollowing}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
              >
                <div style={{ 
                  fontSize: '2rem', 
                  fontWeight: 'bold', 
                  background: 'linear-gradient(135deg, #FF6B6B, #EE5A6F)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  {profile.followingCount || 0}
                </div>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-gray)', marginTop: '4px', fontWeight: '500' }}>Following</div>
              </div>
              <div style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div>
                  <div style={{ fontSize: '0.9rem', color: 'var(--text-muted)', fontWeight: '500' }}>
                    📅 Member since
                  </div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '4px' }}>
                    {new Date(profile.createdAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </div>
                </div>
              </div>
            </div>

            {/* Featured Achievements */}
            {featuredAchievements.length > 0 && (
              <div style={{
                marginTop: '30px',
                padding: '20px',
                backgroundColor: 'rgba(255, 255, 255, 0.03)',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.05)'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '15px'
                }}>
                  <h3 style={{ margin: 0, color: 'var(--text)', fontSize: '1.2rem' }}>
                    🏆 Featured Achievements
                  </h3>
                  <button
                    onClick={() => navigate('/achievements')}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--primary)',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      textDecoration: 'underline'
                    }}
                  >
                    View All →
                  </button>
                </div>
                <div style={{
                  display: 'flex',
                  gap: '15px',
                  flexWrap: 'wrap',
                  justifyContent: 'center'
                }}>
                  {featuredAchievements.map((achievement) => (
                    <Badge
                      key={achievement.id}
                      achievement={achievement}
                      size="medium"
                      unlocked={true}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignSelf: 'start' }}>
            {isOwnProfile ? (
              <button
                onClick={() => navigate('/profile/edit')}
                style={{
                  padding: '14px 28px',
                  background: 'linear-gradient(135deg, #2196F3, #1976D2)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 15px rgba(33, 150, 243, 0.3)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(33, 150, 243, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 15px rgba(33, 150, 243, 0.3)';
                }}
              >
                ✏️ Edit Profile
              </button>
            ) : (
              <>
                <button
                  onClick={handleFollowToggle}
                  disabled={followLoading}
                  style={{
                    padding: '14px 28px',
                    background: isFollowing 
                      ? 'linear-gradient(135deg, #6c757d, #5a6268)'
                      : 'linear-gradient(135deg, #9C27B0, #7B1FA2)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: followLoading ? 'not-allowed' : 'pointer',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    boxShadow: isFollowing 
                      ? '0 4px 15px rgba(108, 117, 125, 0.3)'
                      : '0 4px 15px rgba(156, 39, 176, 0.3)',
                    transition: 'all 0.3s ease',
                    opacity: followLoading ? 0.7 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!followLoading) {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = isFollowing
                        ? '0 6px 20px rgba(108, 117, 125, 0.4)'
                        : '0 6px 20px rgba(156, 39, 176, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = isFollowing
                      ? '0 4px 15px rgba(108, 117, 125, 0.3)'
                      : '0 4px 15px rgba(156, 39, 176, 0.3)';
                  }}
                >
                  {followLoading ? '...' : isFollowing ? '✓ Following' : '+ Follow'}
                </button>
                <button
                  onClick={handleMessageUser}
                  style={{
                    padding: '14px 28px',
                    background: 'linear-gradient(135deg, #2196F3, #1976D2)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    boxShadow: '0 4px 15px rgba(33, 150, 243, 0.3)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(33, 150, 243, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 15px rgba(33, 150, 243, 0.3)';
                  }}
                >
                  💬 Message
                </button>
                <button
                  onClick={() => setShowVouchModal(true)}
                  style={{
                    padding: '14px 28px',
                    background: 'linear-gradient(135deg, #ffd700, #ffed4e)',
                    color: '#1a1a1a',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    boxShadow: '0 4px 15px rgba(255, 215, 0, 0.3)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(255, 215, 0, 0.5)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 15px rgba(255, 215, 0, 0.3)';
                  }}
                >
                  ⭐ Vouch
                </button>
              </>
            )}
          </div>
        </div>

        {/* Social Links */}
        {profile.profile?.socialLinks && profile.profile.socialLinks.length > 0 && (
          <div style={{ 
            marginTop: '30px',
            paddingTop: '30px',
            borderTop: '1px solid var(--border-color)'
          }}>
            <h3 style={{ 
              color: 'var(--text-primary)', 
              marginBottom: '20px', 
              fontSize: '1.3rem',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #4CAF50, #45a049)'
              }}>
                🔗
              </span>
              Social Links
            </h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              {profile.profile.socialLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '12px 20px',
                    backgroundColor: getPlatformColor(link.platform),
                    color: 'white',
                    borderRadius: '12px',
                    textDecoration: 'none',
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    transition: 'all 0.3s ease',
                    border: 'none',
                    boxShadow: `0 4px 12px ${getPlatformColor(link.platform)}40`
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = `0 6px 20px ${getPlatformColor(link.platform)}60`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = `0 4px 12px ${getPlatformColor(link.platform)}40`;
                  }}
                >
                  <span style={{ fontSize: '1.2rem' }}>{getPlatformIcon(link.platform)}</span>
                  <span>{link.displayName || link.platform}</span>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Verified Accounts */}
        <div style={{ 
          marginTop: '30px',
          paddingTop: '30px',
          borderTop: '1px solid var(--border-color)'
        }}>
          <h3 style={{ 
            color: 'var(--text-primary)', 
            marginBottom: '20px', 
            fontSize: '1.3rem',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #2196F3, #1976D2)'
            }}>
              ✓
            </span>
            Verified Accounts
          </h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            {/* Steam Verification */}
            {profile.socialAccounts?.steam?.verified ? (
              (profile.verifiedAccountsPublic || isAdmin) ? (
                <a 
                  href={profile.socialAccounts.steam.profileUrl || `https://steamcommunity.com/profiles/${profile.socialAccounts.steam.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '12px 20px',
                    backgroundColor: 'rgba(23, 26, 33, 0.6)',
                    color: 'white',
                    borderRadius: '12px',
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    border: '2px solid #1b2838',
                    backdropFilter: 'blur(10px)',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    transform: 'scale(1)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(27, 40, 56, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <span style={{ fontSize: '1.2rem' }}>🎮</span>
                  <span>Steam</span>
                  <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 10px',
                    background: 'linear-gradient(135deg, #4CAF50, #45a049)',
                    borderRadius: '12px',
                    fontSize: '0.8rem',
                    fontWeight: 'bold'
                  }}>
                    ✓ Verified
                  </span>
                </a>
              ) : (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '12px 20px',
                  backgroundColor: 'rgba(23, 26, 33, 0.6)',
                  color: 'white',
                  borderRadius: '12px',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  border: '2px solid #1b2838',
                  backdropFilter: 'blur(10px)',
                  cursor: 'default'
                }}>
                  <span style={{ fontSize: '1.2rem' }}>🎮</span>
                  <span>Steam</span>
                  <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 10px',
                    background: 'linear-gradient(135deg, #4CAF50, #45a049)',
                    borderRadius: '12px',
                    fontSize: '0.8rem',
                    fontWeight: 'bold'
                  }}>
                    ✓ Verified
                  </span>
                </div>
              )
            ) : (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '12px 20px',
                backgroundColor: 'rgba(60, 60, 60, 0.4)',
                color: 'white',
                borderRadius: '12px',
                fontSize: '0.95rem',
                fontWeight: '600',
                border: '2px solid rgba(100, 100, 100, 0.3)',
                backdropFilter: 'blur(10px)'
              }}>
                <span style={{ fontSize: '1.2rem' }}>🎮</span>
                <span>Steam</span>
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 10px',
                  background: 'rgba(120, 120, 120, 0.4)',
                  borderRadius: '12px',
                  fontSize: '0.8rem',
                  opacity: 0.7
                }}>
                  ✗ Not Verified
                </span>
              </div>
            )}

            {/* Discord Verification */}
            {profile.socialAccounts?.discord?.verified ? (
              (profile.verifiedAccountsPublic || isAdmin) ? (
                <a
                  href={`https://discord.com/users/${profile.socialAccounts.discord.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '12px 20px',
                    backgroundColor: 'rgba(114, 137, 218, 0.2)',
                    color: 'white',
                    borderRadius: '12px',
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    border: '2px solid #7289da',
                    backdropFilter: 'blur(10px)',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    transform: 'scale(1)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(114, 137, 218, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <span style={{ fontSize: '1.2rem' }}>💬</span>
                  <span>Discord</span>
                  <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 10px',
                    background: 'linear-gradient(135deg, #4CAF50, #45a049)',
                    borderRadius: '12px',
                    fontSize: '0.8rem',
                    fontWeight: 'bold'
                  }}>
                    ✓ Verified
                  </span>
                </a>
              ) : (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '12px 20px',
                  backgroundColor: 'rgba(114, 137, 218, 0.2)',
                  color: 'white',
                  borderRadius: '12px',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  border: '2px solid #7289da',
                  backdropFilter: 'blur(10px)',
                  cursor: 'default'
                }}>
                  <span style={{ fontSize: '1.2rem' }}>💬</span>
                  <span>Discord</span>
                  <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 10px',
                    background: 'linear-gradient(135deg, #4CAF50, #45a049)',
                    borderRadius: '12px',
                    fontSize: '0.8rem',
                    fontWeight: 'bold'
                  }}>
                    ✓ Verified
                  </span>
                </div>
              )
            ) : (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '12px 20px',
                backgroundColor: 'rgba(60, 60, 60, 0.4)',
                color: 'white',
                borderRadius: '12px',
                fontSize: '0.95rem',
                fontWeight: '600',
                border: '2px solid rgba(100, 100, 100, 0.3)',
                backdropFilter: 'blur(10px)'
              }}>
                <span style={{ fontSize: '1.2rem' }}>💬</span>
                <span>Discord</span>
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 10px',
                  background: 'rgba(120, 120, 120, 0.4)',
                  borderRadius: '12px',
                  fontSize: '0.8rem',
                  opacity: 0.7
                }}>
                  ✗ Not Verified
                </span>
              </div>
            )}

            {/* Twitter Verification */}
            {profile.socialAccounts?.twitter?.verified ? (
              (profile.verifiedAccountsPublic || isAdmin) ? (
                <a
                  href={`https://twitter.com/${profile.socialAccounts.twitter.username}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '12px 20px',
                    backgroundColor: 'rgba(29, 161, 242, 0.2)',
                    color: 'white',
                    borderRadius: '12px',
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    border: '2px solid #1da1f2',
                    backdropFilter: 'blur(10px)',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    transform: 'scale(1)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(29, 161, 242, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <span style={{ fontSize: '1.2rem' }}>🐦</span>
                  <span>Twitter</span>
                  <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 10px',
                    background: 'linear-gradient(135deg, #4CAF50, #45a049)',
                    borderRadius: '12px',
                    fontSize: '0.8rem',
                    fontWeight: 'bold'
                  }}>
                    ✓ Verified
                  </span>
                </a>
              ) : (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '12px 20px',
                  backgroundColor: 'rgba(29, 161, 242, 0.2)',
                  color: 'white',
                  borderRadius: '12px',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  border: '2px solid #1da1f2',
                  backdropFilter: 'blur(10px)',
                  cursor: 'default'
                }}>
                  <span style={{ fontSize: '1.2rem' }}>🐦</span>
                  <span>Twitter</span>
                  <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 10px',
                    background: 'linear-gradient(135deg, #4CAF50, #45a049)',
                    borderRadius: '12px',
                    fontSize: '0.8rem',
                    fontWeight: 'bold'
                  }}>
                    ✓ Verified
                  </span>
                </div>
              )
            ) : (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '12px 20px',
                backgroundColor: 'rgba(60, 60, 60, 0.4)',
                color: 'white',
                borderRadius: '12px',
                fontSize: '0.95rem',
                fontWeight: '600',
                border: '2px solid rgba(100, 100, 100, 0.3)',
                backdropFilter: 'blur(10px)'
              }}>
                <span style={{ fontSize: '1.2rem' }}>🐦</span>
                <span>Twitter</span>
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 10px',
                  background: 'rgba(120, 120, 120, 0.4)',
                  borderRadius: '12px',
                  fontSize: '0.8rem',
                  opacity: 0.7
                }}>
                  ✗ Not Verified
                </span>
              </div>
            )}

            {/* Bluesky Verification */}
            {profile.socialAccounts?.bluesky?.verified ? (
              (profile.verifiedAccountsPublic || isAdmin) ? (
                <a
                  href={`https://bsky.app/profile/${profile.socialAccounts.bluesky.handle}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '12px 20px',
                    backgroundColor: 'rgba(17, 133, 254, 0.2)',
                    color: 'white',
                    borderRadius: '12px',
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    border: '2px solid #1185fe',
                    backdropFilter: 'blur(10px)',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    transform: 'scale(1)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(17, 133, 254, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <span style={{ fontSize: '1.2rem' }}>🦋</span>
                  <span>Bluesky</span>
                  <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 10px',
                    background: 'linear-gradient(135deg, #4CAF50, #45a049)',
                    borderRadius: '12px',
                    fontSize: '0.8rem',
                    fontWeight: 'bold'
                  }}>
                    ✓ Verified
                  </span>
                </a>
              ) : (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '12px 20px',
                  backgroundColor: 'rgba(17, 133, 254, 0.2)',
                  color: 'white',
                  borderRadius: '12px',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  border: '2px solid #1185fe',
                  backdropFilter: 'blur(10px)',
                  cursor: 'default'
                }}>
                  <span style={{ fontSize: '1.2rem' }}>🦋</span>
                  <span>Bluesky</span>
                  <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 10px',
                    background: 'linear-gradient(135deg, #4CAF50, #45a049)',
                    borderRadius: '12px',
                    fontSize: '0.8rem',
                    fontWeight: 'bold'
                  }}>
                    ✓ Verified
                  </span>
                </div>
              )
            ) : null}
          </div>
        </div>
      </div>

      {/* Tabs Section */}
      <div style={{ marginTop: '30px' }}>
        {/* Tab Buttons */}
        <div style={{ 
          display: 'flex', 
          gap: '10px', 
          marginBottom: '25px',
          borderBottom: '2px solid #2d2d2d',
          paddingBottom: '10px',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={() => setActiveTab('posts')}
            style={{
              padding: '12px 24px',
              background: activeTab === 'posts' 
                ? 'linear-gradient(135deg, #667eea, #764ba2)' 
                : 'transparent',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: 'bold',
              transition: 'all 0.3s ease',
              boxShadow: activeTab === 'posts' 
                ? '0 4px 15px rgba(102, 126, 234, 0.4)' 
                : 'none'
            }}
          >
            📝 Posts
          </button>
          <button
            onClick={() => setActiveTab('giveaways')}
            style={{
              padding: '12px 24px',
              background: activeTab === 'giveaways' 
                ? 'linear-gradient(135deg, #FF5722, #E64A19)' 
                : 'transparent',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: 'bold',
              transition: 'all 0.3s ease',
              boxShadow: activeTab === 'giveaways' 
                ? '0 4px 15px rgba(255, 87, 34, 0.3)' 
                : 'none'
            }}
          >
            🎁 Giveaways
          </button>
          <button
            onClick={() => setActiveTab('lists')}
            style={{
              padding: '12px 24px',
              background: activeTab === 'lists' 
                ? 'linear-gradient(135deg, #673AB7, #512DA8)' 
                : 'transparent',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: 'bold',
              transition: 'all 0.3s ease',
              boxShadow: activeTab === 'lists' 
                ? '0 4px 15px rgba(103, 58, 183, 0.3)' 
                : 'none'
            }}
          >
            📋 Lists
          </button>
          <button
            onClick={() => setActiveTab('showcase')}
            style={{
              padding: '12px 24px',
              background: activeTab === 'showcase' 
                ? 'linear-gradient(135deg, #9C27B0, #7B1FA2)' 
                : 'transparent',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: 'bold',
              transition: 'all 0.3s ease',
              boxShadow: activeTab === 'showcase' 
                ? '0 4px 15px rgba(156, 39, 176, 0.3)' 
                : 'none'
            }}
          >
            🎨 Showcase
          </button>
          <button
            onClick={() => setActiveTab('vouches')}
            style={{
              padding: '12px 24px',
              background: activeTab === 'vouches' 
                ? 'linear-gradient(135deg, #FFC107, #FFA000)' 
                : 'transparent',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: 'bold',
              transition: 'all 0.3s ease',
              boxShadow: activeTab === 'vouches' 
                ? '0 4px 15px rgba(255, 193, 7, 0.3)' 
                : 'none'
            }}
          >
            ⭐ Vouches ({profile?.profile?.vouchCount || 0})
          </button>
          {isOwnProfile && (
            <button
              onClick={() => setActiveTab('winners')}
              style={{
                padding: '12px 24px',
                background: activeTab === 'winners' 
                  ? 'linear-gradient(135deg, #00BCD4, #0097A7)' 
                  : 'transparent',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 'bold',
                transition: 'all 0.3s ease',
                boxShadow: activeTab === 'winners' 
                  ? '0 4px 15px rgba(0, 188, 212, 0.3)' 
                  : 'none'
              }}
            >
              🏆 Winners
            </button>
          )}
          {isOwnProfile && activeTab === 'showcase' && (
            <button
              onClick={() => navigate('/showcase/create')}
              style={{
                padding: '12px 24px',
                background: 'linear-gradient(135deg, #4CAF50, #45a049)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 'bold',
                marginLeft: 'auto',
                boxShadow: '0 4px 15px rgba(76, 175, 80, 0.3)'
              }}
            >
              + Add Showcase
            </button>
          )}
          {isOwnProfile && activeTab === 'lists' && (
            <button
              onClick={() => navigate('/create-list')}
              style={{
                width: '140px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #4CAF50, #45a049)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: 'bold',
                marginLeft: 'auto',
                boxShadow: '0 4px 15px rgba(76, 175, 80, 0.3)'
              }}
            >
              + Create List
            </button>
          )}
        </div>

        {/* Posts Tab */}
        {activeTab === 'posts' && (
          <div>
            {posts.length === 0 ? (
              <div style={{
                background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
                borderRadius: '20px',
                padding: '60px 40px',
                textAlign: 'center',
                border: `1px solid var(--modal-border)`,
                boxShadow: 'var(--shadow-hover)'
              }}>
                <div style={{ fontSize: '4rem', marginBottom: '20px', opacity: '0.5' }}>📝</div>
                <p style={{ color: 'var(--text-muted)', fontSize: '1.2rem', margin: 0 }}>
                  No posts yet. Share something with the community!
                </p>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 500px), 1fr))',
                gap: '24px'
              }}>
                {posts.map((item) => (
                  <div
                    key={item._id}
                    style={{
                      background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
                      borderRadius: '20px',
                      padding: '24px',
                      border: `1px solid var(--modal-border)`,
                      boxShadow: 'var(--shadow-hover)',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '16px'
                    }}>
                      <div style={{ flex: 1 }}>
                        {item.content && (
                          <p style={{
                            color: 'var(--text)',
                            marginBottom: '16px',
                            lineHeight: '1.6',
                            whiteSpace: 'pre-wrap'
                          }}>
                            {item.content}
                          </p>
                        )}
                      </div>
                      {isOwnProfile && (
                        <button
                          onClick={() => handleDeletePost(item._id)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#ff4444',
                            cursor: 'pointer',
                            fontSize: '1.2rem',
                            padding: '4px 8px'
                          }}
                        >
                          🗑️
                        </button>
                      )}
                    </div>

                    {item.images && item.images.length > 0 && (
                      <div style={{ marginBottom: '16px', position: 'relative' }}>
                        <img
                          src={item.images[postImageIndices[item._id] || 0]?.url}
                          alt="Post image"
                          style={{
                            width: '100%',
                            height: 'auto',
                            maxHeight: '400px',
                            objectFit: 'cover',
                            borderRadius: '12px'
                          }}
                          onError={(e) => {
                            console.error('Image failed to load:', item.images[postImageIndices[item._id] || 0]);
                            e.target.style.display = 'none';
                          }}
                        />
                        {item.images.length > 1 && (
                          <>
                            <div style={{
                              position: 'absolute',
                              top: '50%',
                              left: 0,
                              right: 0,
                              display: 'flex',
                              justifyContent: 'space-between',
                              padding: '0 10px',
                              transform: 'translateY(-50%)',
                              pointerEvents: 'none'
                            }}>
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  changePostImage(item._id, 'prev');
                                }}
                                style={{
                                  background: 'rgba(0, 0, 0, 0.7)',
                                  border: 'none',
                                  color: 'white',
                                  padding: '10px',
                                  borderRadius: '50%',
                                  cursor: 'pointer',
                                  fontSize: '1.2rem',
                                  pointerEvents: 'auto',
                                  transition: 'background 0.2s'
                                }}
                                onMouseEnter={(e) => e.target.style.background = 'rgba(0, 0, 0, 0.9)'}
                                onMouseLeave={(e) => e.target.style.background = 'rgba(0, 0, 0, 0.7)'}
                              >
                                ←
                              </button>
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  changePostImage(item._id, 'next');
                                }}
                                style={{
                                  background: 'rgba(0, 0, 0, 0.7)',
                                  border: 'none',
                                  color: 'white',
                                  padding: '10px',
                                  borderRadius: '50%',
                                  cursor: 'pointer',
                                  fontSize: '1.2rem',
                                  pointerEvents: 'auto',
                                  transition: 'background 0.2s'
                                }}
                                onMouseEnter={(e) => e.target.style.background = 'rgba(0, 0, 0, 0.9)'}
                                onMouseLeave={(e) => e.target.style.background = 'rgba(0, 0, 0, 0.7)'}
                              >
                                →
                              </button>
                            </div>
                            <div style={{
                              position: 'absolute',
                              bottom: '10px',
                              right: '10px',
                              background: 'rgba(0, 0, 0, 0.7)',
                              padding: '4px 12px',
                              borderRadius: '12px',
                              fontSize: '0.9rem',
                              color: 'white'
                            }}>
                              {(postImageIndices[item._id] || 0) + 1} / {item.images.length}
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      paddingTop: '12px',
                      borderTop: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <button
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: 'var(--accent)',
                          cursor: 'pointer',
                          fontSize: '1rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        ❤️ {item.likeCount || 0}
                      </button>
                      <span style={{
                        color: 'var(--text-muted)',
                        fontSize: '0.9rem'
                      }}>
                        💬 {item.commentCount || 0}
                      </span>
                      <span style={{
                        color: 'var(--text-muted)',
                        fontSize: '0.9rem'
                      }}>
                        {new Date(item.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Giveaways Tab */}
        {activeTab === 'giveaways' && (
          <div>
            {giveaways.length === 0 ? (
              <div style={{
                background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
                borderRadius: '20px',
                padding: '60px 40px',
                textAlign: 'center',
                border: `1px solid var(--modal-border)`,
                boxShadow: 'var(--shadow-hover)'
              }}>
                <div style={{ fontSize: '4rem', marginBottom: '20px', opacity: '0.5' }}>🎁</div>
                <p style={{ color: 'var(--text-muted)', fontSize: '1.2rem', margin: 0 }}>
                  No giveaways hosted yet. Check back later!
                </p>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                gap: '24px'
              }}>
                {giveaways.map(giveaway => (
                  <GiveawayCard
                    key={giveaway._id}
                    giveaway={giveaway}
                    hasEntered={false}
                    onEntryComplete={() => {}}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Showcase Tab */}
        {activeTab === 'showcase' && (
          <div>
            {showcase.length === 0 ? (
              <div style={{
                background: `linear-gradient(135deg, var(--profile-gradient-start) 0%, var(--profile-gradient-end) 100%)`,
                borderRadius: '20px',
                padding: '60px 40px',
                textAlign: 'center',
                border: `1px solid var(--modal-border)`,
                boxShadow: 'var(--shadow-hover)'
              }}>
                <div style={{ fontSize: '4rem', marginBottom: '20px', opacity: '0.5' }}>🎨</div>
                <p style={{ color: 'var(--text-muted)', fontSize: '1.2rem', margin: 0 }}>
                  {isOwnProfile 
                    ? 'No showcase items yet. Click "Add Showcase" to get started!' 
                    : 'No showcase items yet. Check back later!'}
                </p>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                gap: '24px'
              }}>
                {showcase.map((item) => {
                  const currentImageIndex = showcaseImageIndices[item._id] || 0;

                  return (
                    <div
                      key={item._id}
                      style={{
                        background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
                        borderRadius: '16px',
                        overflow: 'hidden',
                        border: '1px solid #3d3d3d',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                        transition: 'transform 0.3s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                    >
                      {/* Image Gallery with Navigation */}
                      {item.images && item.images.length > 0 && (
                        <div style={{ position: 'relative', height: '250px', overflow: 'hidden', backgroundColor: 'var(--banner-placeholder-bg)' }}>
                          <img
                            src={item.images[currentImageIndex].url}
                            alt={`${item.title} - ${currentImageIndex + 1} of ${item.images.length}`}
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                          />
                          
                          {/* Image Counter */}
                          <div style={{
                            position: 'absolute',
                            top: '10px',
                            right: '10px',
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            padding: '6px 12px',
                            borderRadius: '8px',
                            color: 'white',
                            fontSize: '0.9rem',
                            fontWeight: 'bold'
                          }}>
                            📷 {currentImageIndex + 1} / {item.images.length}
                          </div>

                          {/* Navigation Arrows */}
                          {item.images.length > 1 && (
                            <>
                              <button
                                onClick={() => changeShowcaseImage(item._id, 'prev')}
                                style={{
                                  position: 'absolute',
                                  left: '10px',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '50%',
                                  width: '40px',
                                  height: '40px',
                                  cursor: 'pointer',
                                  fontSize: '1.2rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.9)'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.7)'}
                              >
                                ‹
                              </button>
                              <button
                                onClick={() => changeShowcaseImage(item._id, 'next')}
                                style={{
                                  position: 'absolute',
                                  right: '10px',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '50%',
                                  width: '40px',
                                  height: '40px',
                                  cursor: 'pointer',
                                  fontSize: '1.2rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.9)'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.7)'}
                              >
                                ›
                              </button>

                              {/* Image Dots Indicator */}
                              <div style={{
                                position: 'absolute',
                                bottom: '10px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                display: 'flex',
                                gap: '6px'
                              }}>
                                {item.images.map((_, idx) => (
                                  <button
                                    key={idx}
                                    onClick={() => setShowcaseImageIndices(prev => ({ ...prev, [item._id]: idx }))}
                                    style={{
                                      width: '8px',
                                      height: '8px',
                                      borderRadius: '50%',
                                      border: 'none',
                                      backgroundColor: idx === currentImageIndex ? '#fff' : 'rgba(255, 255, 255, 0.4)',
                                      cursor: 'pointer',
                                      padding: 0,
                                      transition: 'all 0.3s ease'
                                    }}
                                  />
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      )}

                    {/* Content */}
                    <div style={{ padding: '20px' }}>
                      <h3 style={{ margin: '0 0 10px 0', color: 'var(--text-dark)', fontSize: '1.3rem' }}>
                        {item.title}
                      </h3>
                      {item.description && (
                        <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6', marginBottom: '15px' }}>
                          {item.description}
                        </p>
                      )}

                      {/* Actions */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginTop: '15px' }}>
                        <button
                          onClick={() => handleUpvote(item._id)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '8px 16px',
                            background: item.upvotes?.includes(user?._id) 
                              ? 'linear-gradient(135deg, #FF5722, #E64A19)' 
                              : 'rgba(255, 255, 255, 0.1)',
                            color: 'white',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: 'bold',
                            transition: 'all 0.3s ease'
                          }}
                        >
                          👍 {item.upvoteCount || 0}
                        </button>

                        {isOwnProfile && (
                          <button
                            onClick={() => handleDeleteShowcase(item._id)}
                            style={{
                              padding: '8px 16px',
                              background: 'rgba(255, 87, 34, 0.2)',
                              color: 'var(--error-text)',
                              border: '1px solid var(--error-text)',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontSize: '0.9rem',
                              fontWeight: 'bold',
                              marginLeft: 'auto'
                            }}
                          >
                            🗑️ Delete
                          </button>
                        )}
                      </div>

                      {/* Comments Section */}
                      <ShowcaseComments showcaseId={item._id} />
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Vouches Tab */}
        {activeTab === 'vouches' && (
          <div>
            {vouchStats && (
              <div style={{
                background: 'linear-gradient(135deg, #FFC107, #FFA000)',
                borderRadius: '16px',
                padding: '20px',
                marginBottom: '20px',
                display: 'flex',
                gap: '30px',
                justifyContent: 'space-around',
                flexWrap: 'wrap'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white' }}>
                    {vouchStats.total}
                  </div>
                  <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.9rem' }}>
                    Total Vouches
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white' }}>
                    {vouchStats.averageRating}⭐
                  </div>
                  <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.9rem' }}>
                    Average Rating
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white' }}>
                    {vouchStats.verified}
                  </div>
                  <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.9rem' }}>
                    Verified Vouches
                  </div>
                </div>
              </div>
            )}

            {vouches.length === 0 ? (
              <div style={{
                background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
                borderRadius: '20px',
                padding: '60px 40px',
                textAlign: 'center',
                border: '1px solid #3d3d3d'
              }}>
                <div style={{ fontSize: '4rem', marginBottom: '20px', opacity: '0.5' }}>⭐</div>
                <p style={{ color: '#999', fontSize: '1.2rem', margin: 0 }}>
                  No vouches yet
                </p>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gap: '16px'
              }}>
                {vouches.map((vouch) => (
                  <div
                    key={vouch._id}
                    style={{
                      background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
                      borderRadius: '16px',
                      padding: '20px',
                      border: '1px solid #3d3d3d',
                      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                      {vouch.from.profile?.avatar?.url && !vouchImageErrors[vouch._id] ? (
                        <img
                          src={vouch.from.profile.avatar.url}
                          alt={vouch.from.username}
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            objectFit: 'cover'
                          }}
                          onError={() => setVouchImageErrors(prev => ({ ...prev, [vouch._id]: true }))}
                        />
                      ) : (
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          background: 'var(--btn-primary)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'bold',
                          color: 'white',
                          border: '2px solid rgba(255,255,255,0.1)'
                        }}>
                          {vouch.from.username.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <strong style={{ color: 'white' }}>{vouch.from.username}</strong>
                          {vouch.verified && (
                            <span style={{
                              background: 'linear-gradient(135deg, #4CAF50, #45a049)',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              color: 'white'
                            }}>
                              ✓ Verified
                            </span>
                          )}
                        </div>
                        <div style={{ color: '#999', fontSize: '0.85rem' }}>
                          {new Date(vouch.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ color: '#FFC107', fontSize: '1.1rem', marginBottom: '4px' }}>
                        {'⭐'.repeat(vouch.rating || 0)}
                      </div>
                      <div style={{ color: 'white', fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '4px' }}>
                        🎮 {vouch.gameName}
                      </div>
                      {(vouch.codeRequest || vouch.giveaway) && (
                        <div style={{ color: '#999', fontSize: '0.85rem' }}>
                          {vouch.codeRequest && '📦 From Code Offer'}
                          {vouch.giveaway && '🎁 From Giveaway'}
                        </div>
                      )}
                    </div>
                    
                    <p style={{ color: '#ccc', lineHeight: '1.6', margin: '0 0 12px 0' }}>
                      "{vouch.message}"
                    </p>

                    {vouch.screenshot && (
                      <div style={{ marginTop: '12px' }}>
                        <div style={{ 
                          color: '#999', 
                          fontSize: '0.85rem', 
                          marginBottom: '8px',
                          fontWeight: 600
                        }}>
                          📸 Proof:
                        </div>
                        <img
                          src={vouch.screenshot}
                          alt="Vouch proof"
                          style={{
                            width: '100%',
                            maxHeight: '300px',
                            objectFit: 'contain',
                            borderRadius: '8px',
                            border: '1px solid rgba(102, 126, 234, 0.3)',
                            background: 'rgba(0, 0, 0, 0.5)',
                            cursor: 'pointer'
                          }}
                          onClick={() => window.open(vouch.screenshot, '_blank')}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Winners Tab (Own Profile Only) */}
        {activeTab === 'winners' && isOwnProfile && (
          <div>
            {winnersStats && (
              <div style={{
                background: 'linear-gradient(135deg, #00BCD4, #0097A7)',
                borderRadius: '16px',
                padding: '20px',
                marginBottom: '20px',
                display: 'flex',
                gap: '30px',
                justifyContent: 'space-around',
                flexWrap: 'wrap'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white' }}>
                    {winnersStats.totalGiveaways}
                  </div>
                  <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.9rem' }}>
                    Giveaways with Winners
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white' }}>
                    {winnersStats.totalWinners}
                  </div>
                  <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.9rem' }}>
                    Total Winners
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white' }}>
                    {winnersStats.vouchRate}%
                  </div>
                  <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.9rem' }}>
                    Vouch Rate
                  </div>
                </div>
              </div>
            )}

            {winners.length === 0 ? (
              <div style={{
                background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
                borderRadius: '20px',
                padding: '60px 40px',
                textAlign: 'center',
                border: '1px solid #3d3d3d'
              }}>
                <div style={{ fontSize: '4rem', marginBottom: '20px', opacity: '0.5' }}>🏆</div>
                <p style={{ color: '#999', fontSize: '1.2rem', margin: 0 }}>
                  No winners yet from your giveaways
                </p>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gap: '16px'
              }}>
                {winners.map((winner) => (
                  <div
                    key={`${winner.giveawayId}-${winner.winnerId}-${winner.position}`}
                    style={{
                      background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
                      borderRadius: '16px',
                      padding: '20px',
                      border: winner.hasVouched ? '1px solid #4CAF50' : '1px solid #3d3d3d',
                      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                      <div>
                        <div style={{ color: 'white', fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '4px' }}>
                          {winner.giveawayTitle}
                        </div>
                        <div style={{ color: '#999', fontSize: '0.9rem' }}>
                          🎁 {winner.giveawayPrize}
                        </div>
                      </div>
                      {winner.hasVouched ? (
                        <span style={{
                          background: 'linear-gradient(135deg, #4CAF50, #45a049)',
                          padding: '6px 12px',
                          borderRadius: '8px',
                          fontSize: '0.9rem',
                          color: 'white',
                          whiteSpace: 'nowrap'
                        }}>
                          ✓ Vouched
                        </span>
                      ) : (
                        <span style={{
                          background: 'rgba(255, 152, 0, 0.2)',
                          padding: '6px 12px',
                          borderRadius: '8px',
                          fontSize: '0.9rem',
                          color: '#FF9800',
                          border: '1px solid #FF9800',
                          whiteSpace: 'nowrap'
                        }}>
                          ⏳ No Vouch
                        </span>
                      )}
                    </div>

                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '12px',
                      padding: '12px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '8px',
                      marginBottom: winner.hasVouched && winner.vouchData ? '12px' : '0'
                    }}>
                      <div style={{ color: 'white', fontSize: '1.5rem' }}>
                        {winner.position === 1 ? '🥇' : winner.position === 2 ? '🥈' : winner.position === 3 ? '🥉' : '🏆'}
                      </div>
                      <div>
                        <div style={{ color: 'white', fontWeight: 'bold' }}>
                          {winner.winnerUsername}
                        </div>
                        <div style={{ color: '#999', fontSize: '0.85rem' }}>
                          Won on {new Date(winner.rollTimestamp).toLocaleDateString()}
                        </div>
                      </div>
                    </div>

                    {winner.hasVouched && winner.vouchData && (
                      <div style={{
                        padding: '12px',
                        background: 'rgba(76, 175, 80, 0.1)',
                        borderRadius: '8px',
                        border: '1px solid rgba(76, 175, 80, 0.3)'
                      }}>
                        <div style={{ color: '#FFC107', marginBottom: '4px' }}>
                          {'⭐'.repeat(winner.vouchData.rating || 0)}
                        </div>
                        <p style={{ color: '#ccc', margin: 0, fontSize: '0.9rem' }}>
                          "{winner.vouchData.message}"
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Lists Tab */}
      {activeTab === 'lists' && (
        <div>
          {lists.length === 0 ? (
            <div style={{
              background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
              borderRadius: '20px',
              padding: '60px 40px',
              textAlign: 'center',
              border: `1px solid var(--modal-border)`,
              boxShadow: 'var(--shadow-hover)'
            }}>
              <div style={{ fontSize: '4rem', marginBottom: '20px', opacity: '0.5' }}>📋</div>
              <p style={{ color: 'var(--text-muted)', fontSize: '1.2rem', margin: 0 }}>
                {isOwnProfile ? "You haven't created any lists yet." : "This user hasn't created any lists yet."}
              </p>
              {isOwnProfile && (
                <button
                  onClick={() => navigate('/create-list')}
                  style={{
                    marginTop: '20px',
                    padding: '12px 24px',
                    backgroundColor: 'var(--accent-green)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: 'bold'
                  }}
                >
                  Create Your First List
                </button>
              )}
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '24px'
            }}>
              {lists.map(list => (
                <div
                  key={list._id}
                  onClick={() => navigate(`/lists/${list._id}`)}
                  style={{
                    backgroundColor: 'var(--card-bg)',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    border: '1px solid var(--border-color)',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = 'var(--shadow-hover)';
                    e.currentTarget.style.borderColor = 'var(--accent-purple)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = 'var(--border-color)';
                  }}
                >
                  {/* List Preview Images */}
                  <div style={{
                    height: '160px',
                    backgroundColor: '#2a2a2a',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    {list.games.length > 0 ? (
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: list.games.length >= 3 ? '2fr 1fr' : '1fr',
                        height: '100%'
                      }}>
                        {/* Main Image */}
                        <div style={{ position: 'relative', height: '100%' }}>
                          <img
                            src={list.games[0].game.coverUrl || 'https://via.placeholder.com/300x400?text=No+Cover'}
                            alt=""
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        </div>
                        {/* Side Images (if 3+ games) */}
                        {list.games.length >= 3 && (
                          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                            <div style={{ flex: 1, position: 'relative' }}>
                              <img
                                src={list.games[1].game.coverUrl || 'https://via.placeholder.com/300x400?text=No+Cover'}
                                alt=""
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover'
                                }}
                              />
                            </div>
                            <div style={{ flex: 1, position: 'relative' }}>
                              <img
                                src={list.games[2].game.coverUrl || 'https://via.placeholder.com/300x400?text=No+Cover'}
                                alt=""
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover'
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        color: 'var(--text-muted)',
                        fontSize: '3rem'
                      }}>
                        📋
                      </div>
                    )}
                    {/* Game Count Badge */}
                    <div style={{
                      position: 'absolute',
                      bottom: '10px',
                      right: '10px',
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: '6px',
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      zIndex: 2
                    }}>
                      {list.games.length} games
                    </div>
                    {/* Private Badge */}
                    {!list.isPublic && (
                      <div style={{
                        position: 'absolute',
                        top: '10px',
                        left: '10px',
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        color: '#ff4444',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        zIndex: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        🔒 Private
                      </div>
                    )}
                  </div>

                  <div style={{ padding: '16px' }}>
                    <h3 style={{
                      margin: '0 0 8px 0',
                      fontSize: '1.2rem',
                      color: 'var(--text-primary)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {list.title}
                    </h3>
                    <p style={{
                      margin: '0 0 16px 0',
                      color: 'var(--text-secondary)',
                      fontSize: '0.9rem',
                      lineHeight: '1.4',
                      height: '2.8em',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical'
                    }}>
                      {list.description || 'No description provided'}
                    </p>

                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      color: 'var(--text-muted)',
                      fontSize: '0.85rem'
                    }}>
                      <div style={{ display: 'flex', gap: '12px' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          ❤️ {list.likeCount || 0}
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          📅 {new Date(list.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Vouch Modal */}
      {showVouchModal && profile && (
        <VouchModal
          toUserId={profile._id}
          toUsername={profile.username}
          onClose={() => setShowVouchModal(false)}
          onSuccess={() => {
            setShowVouchModal(false);
            fetchVouches();
            fetchProfile();
          }}
        />
      )}

      {/* Followers Modal */}
      {showFollowersModal && (
        <UserListModal
          title="Followers"
          users={modalUsers}
          loading={modalLoading}
          onClose={() => setShowFollowersModal(false)}
        />
      )}

      {/* Following Modal */}
      {showFollowingModal && (
        <UserListModal
          title="Following"
          users={modalUsers}
          loading={modalLoading}
          onClose={() => setShowFollowingModal(false)}
        />
      )}
    </div>
  );
};

export default Profile;
